import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BarChart3, FileText, Mail } from "lucide-react";
import { useI18n } from "../../i18n";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { COMPANY, LAST_UPDATED, LEGAL } from "../../content/legal";
import { Button } from "../../components/ui/button";

const DOC_ROUTES = [
  { slug: "terms", key: "legal.terms" },
  { slug: "privacy", key: "legal.privacy" },
  { slug: "refund", key: "legal.refund" },
];

const renderInline = (text) => {
  // supports **bold** segments only
  const parts = String(text).split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-foreground">
        {part}
      </strong>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const LegalPage = () => {
  const { doc } = useParams();
  const { t, lang } = useI18n();
  const slug = DOC_ROUTES.some((d) => d.slug === doc) ? doc : "terms";
  const content = LEGAL[slug][lang] || LEGAL[slug].en;

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b bg-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-[900px] items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <BarChart3 className="h-4 w-4" />
            </span>
            <span className="font-display text-base font-semibold">LedgerLens</span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="mr-2 h-4 w-4" /> {t("legal.backHome")}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[900px] px-4 py-10 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          {DOC_ROUTES.map((d) => (
            <Link key={d.slug} to={`/legal/${d.slug}`} data-testid={`legal-tab-${d.slug}`}>
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  d.slug === slug
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:bg-accent"
                }`}
              >
                <FileText className="h-3.5 w-3.5" /> {t(d.key)}
              </span>
            </Link>
          ))}
        </div>

        <h1 className="mt-6 font-display text-3xl font-semibold tracking-tight" data-testid="legal-title">
          {content.title}
        </h1>
        <p className="mt-1 text-xs text-muted-foreground">
          {t("legal.lastUpdated")}: {LAST_UPDATED} · {COMPANY.legalName}
        </p>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{content.intro}</p>

        <div className="mt-8 space-y-7">
          {content.sections.map((s) => (
            <section key={s.h}>
              <h2 className="font-display text-lg font-semibold">{s.h}</h2>
              {s.p ? (
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{renderInline(s.p)}</p>
              ) : null}
              {s.list ? (
                <ul className="mt-2 space-y-1.5">
                  {s.list.map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm leading-relaxed text-muted-foreground">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                      <span>{renderInline(item)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </section>
          ))}
        </div>

        <div className="mt-10 rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Mail className="h-4 w-4 text-primary" /> {t("legal.contact")}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {COMPANY.legalName} · OIB {COMPANY.oib} ·{" "}
            <a href={`mailto:${COMPANY.support}`} className="text-primary hover:underline">
              {COMPANY.support}
            </a>
          </p>
        </div>
      </main>

      <footer className="border-t">
        <div className="mx-auto flex max-w-[900px] flex-col gap-2 px-4 py-6 text-xs text-muted-foreground sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <span>
            © {new Date().getFullYear()} {COMPANY.legalName}
          </span>
          <div className="flex gap-4">
            {DOC_ROUTES.map((d) => (
              <Link key={d.slug} to={`/legal/${d.slug}`} className="hover:text-foreground">
                {t(d.key)}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
